import * as React from "react"
import { Link } from "react-router-dom"
import {
  type ObjectEntities,
  type SearchHit,
  type SearchResult,
} from "@digits-graphql/frontend/graphql-bearer"
import { defined } from "@digits-shared/helpers/filters"
import {
  ExpandableContainer,
  useDimensionAnimation,
} from "@digits-shared/hooks/useDimensionAnimation"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"
import useSearchContext from "src/frontend/components/OS/Applications/Search/SearchContext"
import { ResultSectionHeader } from "src/frontend/components/OS/Applications/Search/SearchResults/ResultSectionHeader"
import { EntityTransactionsTable } from "src/frontend/components/Shared/Transactions/EntityTransactionsTable"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import { SearchResultType } from "src/frontend/types/SearchResultType"
import CategoryResult from "./CategoryResult"
import DepartmentResult from "./DepartmentResult"
import LocationResult from "./LocationResult"
import PartyResult from "./PartyResult"
import UserResult from "./UserResult"

/*
 STYLES
*/

const Section = styled.div`
  border-radius: 14px;
  margin-bottom: 20px;
  border: 1px solid ${colors.white};

  tbody {
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;

    tr:last-child {
      border-bottom-left-radius: inherit;
      border-bottom-right-radius: inherit;
      border-bottom: none;
      td:first-child {
        border-bottom-left-radius: inherit;
      }
      td:last-child {
        border-bottom-right-radius: inherit;
      }
    }
  }
`

const ResultsList = styled.div`
  background: ${colors.cloudWhite}; // sames as tables TBody
  color: ${colors.secondary};
  pointer-events: auto;
  border-radius: 0 0 14px 14px;
`

const NoResultsSection = styled.div`
  padding: 35px 0;
  text-align: center;
  color: ${colors.translucentSecondary70};

  a {
    display: inline-block;
    margin-top: 8px;
    color: ${colors.secondary};
  }
`

/*
 INTERFACES
*/

interface ResultsProps {
  type: SearchResultType
  hits: SearchHit[]
  search: SearchResult
}

interface ResultProps {
  type: SearchResultType
  hit: SearchHit
  entities: ObjectEntities
}

/*
 COMPONENTS
*/

const ResultSection: React.FC<ResultsProps> = ({ type, hits, search }) => (
  <Section>
    <ResultSectionHeader type={type} hits={hits} search={search} />
    <ResultsByType type={type} hits={hits} search={search} />
  </Section>
)

export default ResultSection

const ResultsByType: React.FC<ResultsProps> = ({ type, search, hits }) => {
  const transactions = React.useMemo(() => {
    const entitiesTransactions = new Map(search.entities.transactions?.map((t) => [t.factId, t]))
    return hits.map((h) => entitiesTransactions.get(h.objectId.id)).filter(defined)
  }, [hits, search.entities.transactions])

  switch (type) {
    case SearchResultType.Transaction: {
      return <EntityTransactionsTable transactions={transactions} />
    }

    default:
      return <Results type={type} hits={hits} search={search} />
  }
}

const Results: React.FC<ResultsProps> = ({ type, search, hits }) => {
  const { entities } = search

  const resultsElements = hits
    .map((hit) => <Result key={hit.objectId.id} type={type} hit={hit} entities={entities} />)
    .filter((r): r is React.JSX.Element => r !== null)

  if (!resultsElements.length) {
    resultsElements.push(<NoResults key="no-results" type={type} hits={hits} search={search} />)
  }

  const { style, ref } = useDimensionAnimation("height", { config: { friction: 20 } })

  return (
    <ExpandableContainer style={style}>
      <ResultsList ref={ref}>{resultsElements}</ResultsList>
    </ExpandableContainer>
  )
}

const Result: React.FC<ResultProps> = ({ type, hit, entities }) => {
  switch (type) {
    case SearchResultType.Party: {
      const pEntity = entities.parties?.find((t) => t.id === hit.objectId.id)
      if (!pEntity) {
        TrackJS?.console.error("Hit without an entity", hit)
        return null
      }
      return <PartyResult entity={pEntity} hit={hit} type={type} />
    }

    // TODO: Convert to Report Package
    case SearchResultType.Report:
      return null
    //   const rEntity = entities.reports?.find((t) => t.id === hit.objectId.id)
    //   if (!rEntity) {
    //     TrackJS?.console.error("Hit without an entity", hit)
    //     return null
    //   }
    //   return <ReportResult entity={rEntity} hit={hit} />
    // }

    case SearchResultType.User: {
      const uEntity = entities.users?.find((t) => t.id === hit.objectId.id)
      if (!uEntity) {
        TrackJS?.console.error("Hit without an entity", hit)
        return null
      }
      return <UserResult entity={uEntity} hit={hit} />
    }

    case SearchResultType.Category: {
      const cEntity = entities.categories?.find((t) => t.id === hit.objectId.id)
      if (!cEntity) {
        TrackJS?.console.error("Hit without an entity", hit)
        return null
      }
      return <CategoryResult entity={cEntity} hit={hit} />
    }

    case SearchResultType.Comment:
      return null

    case SearchResultType.Invoice:
      return null

    case SearchResultType.Transaction:
      return null

    case SearchResultType.Department: {
      const dEntity = entities.departments?.find((t) => t.id === hit.objectId.id)
      if (!dEntity) {
        TrackJS?.console.error("Hit without an entity", hit)
        return null
      }
      return <DepartmentResult entity={dEntity} hit={hit} />
    }

    case SearchResultType.Location: {
      const lEntity = entities.locations?.find((t) => t.id === hit.objectId.id)
      if (!lEntity) {
        TrackJS?.console.error("Hit without an entity", hit)
        return null
      }
      return <LocationResult entity={lEntity} hit={hit} />
    }
  }
}

const NoResults: React.FC<ResultsProps> = ({ type }) => {
  const generatePath = useFrontendPathGenerator()
  const { builder } = useSearchContext()

  const { occurredAfter, occurredBefore } = builder

  const clearFiltersParams = builder
    .copy()
    .setOccurredAfter(undefined)
    .setOccurredBefore(undefined)
    .buildUrlParams()

  const path = generatePath(routes.search, clearFiltersParams)

  return (
    <NoResultsSection>
      <div>
        No transactions found
        <NoResultsDates occurredAfter={occurredAfter} occurredBefore={occurredBefore} />
      </div>
      <Link to={path}>Clear Filters</Link>
    </NoResultsSection>
  )
}

const NoResultsDates: React.FC<{
  occurredAfter?: Date
  occurredBefore?: Date
}> = ({ occurredAfter, occurredBefore }) => {
  const after = dayjs(occurredAfter).utc()
  const before = dayjs(occurredBefore).utc()

  if (occurredAfter && occurredBefore) {
    return (
      <>
        <span>&nbsp;between</span>
        <br />
        <b>
          {after.format("MMMM D")} - {before.format("MMMM D")}, {before.format("YYYY")}
        </b>
      </>
    )
  }

  if (occurredAfter && !occurredBefore) {
    return (
      <>
        <span>&nbsp;since&nbsp;</span>
        <b>{after.format("MMMM D, YYYY")}</b>
      </>
    )
  }

  return null
}
